import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BldReportsConfigForm } from '@/features/bld-reports/BldReportsConfigForm';
import {
  type EmailCrawlerProcessorType,
  type EmailCrawlerSource,
  type RecruitmentOutlookStatus,
  createEmailCrawlerSource,
  getRecruitmentOutlookStatus,
  runEmailCrawlerSource,
  updateEmailCrawlerSource,
} from '@/lib/email-crawler-api';
import { API_BASE_URL } from '@/lib/config';
import { VN_TIME_OPTIONS, utcCronToVnTime, vnTimeToUtcCron } from '@/lib/email-crawler-vn-time';
import { toast } from '@/hooks/use-toast';

export interface EmailCrawlerSourceCreateDefaults {
  sourceKey: string;
  processorType: EmailCrawlerProcessorType;
  uiFolderPrefix: string;
}

interface EmailCrawlerSourceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: EmailCrawlerSource | null;
  createDefaults?: EmailCrawlerSourceCreateDefaults | null;
  /**
   * Mở form cấu hình BLD Reports (nguồn SharePoint + lịch chạy + Teams) thay cho cấu hình
   * crawler email — dùng khi người dùng đang ở thư mục S3 `bld-reports/`.
   */
  bldReports?: boolean;
  /**
   * API base runtime của S3 Explorer (prop `apiBaseUrl` khi nhúng vào frontend host,
   * vd `https://dev-hoaphat.cagent.cmcts.ai/s3-explorer`). Dùng cho mọi call API của modal
   * (crawler source, recruitment outlook, BLD Reports); mặc định `API_BASE_URL`
   * (env hoặc host hiện tại).
   */
  apiBaseUrl?: string;
  onSourceUpdated?: (source: EmailCrawlerSource) => void;
  onSourceCreated?: (source: EmailCrawlerSource) => void;
}

const RECRUITMENT_CRON = '* * * * *';

const DEFAULT_DRAFT = {
  sourceKey: '',
  displayName: '',
  mailboxAddress: '',
  uiFolderPrefix: '',
  enabled: false,
  scheduleCron: '0 * * * *',
  timezone: 'Asia/Ho_Chi_Minh',
  lookbackMinutes: 1440,
  maxEmailsPerRun: 50,
  s3Prefix: '',
  subjectFilter: '',
  senderFiltersText: '',
};

type DraftState = typeof DEFAULT_DRAFT;

function buildDraftFromSource(source: EmailCrawlerSource): DraftState {
  return {
    sourceKey: source.sourceKey,
    displayName: source.displayName,
    mailboxAddress: source.mailboxAddress,
    uiFolderPrefix: source.uiFolderPrefix ?? '',
    enabled: source.enabled,
    scheduleCron: source.scheduleCron,
    timezone: source.timezone,
    lookbackMinutes: source.lookbackMinutes,
    maxEmailsPerRun: source.maxEmailsPerRun,
    s3Prefix: source.s3Prefix,
    subjectFilter: source.subjectFilters.join('\n'),
    senderFiltersText: source.senderFilters.join('\n'),
  };
}

function buildDraftFromDefaults(defaults: EmailCrawlerSourceCreateDefaults): DraftState {
  const sourceKey = defaults.sourceKey;
  return {
    ...DEFAULT_DRAFT,
    sourceKey,
    displayName: sourceKey,
    uiFolderPrefix: defaults.uiFolderPrefix,
    s3Prefix: defaults.uiFolderPrefix.replace(/\/$/, ''),
    scheduleCron:
      defaults.processorType === 'recruitment'
        ? RECRUITMENT_CRON
        : DEFAULT_DRAFT.scheduleCron,
  };
}

function normalizeUiFolder(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
}

function isSimplifiedProcessor(processorType: EmailCrawlerProcessorType | undefined): boolean {
  return processorType !== undefined && processorType !== 'email_dashboard';
}

export function EmailCrawlerSourceModal({
  open,
  onOpenChange,
  source,
  createDefaults,
  bldReports = false,
  apiBaseUrl = API_BASE_URL,
  onSourceUpdated,
  onSourceCreated,
}: EmailCrawlerSourceModalProps) {
  const isCreateMode = !source && !!createDefaults;
  const isEditMode = !!source;
  const mode: 'edit' | 'create' | 'empty' = isEditMode
    ? 'edit'
    : isCreateMode
      ? 'create'
      : 'empty';

  const resolvedProcessorType: EmailCrawlerProcessorType | undefined =
    mode === 'edit'
      ? source?.processorType
      : mode === 'create'
        ? createDefaults?.processorType
        : undefined;
  const simplified = isSimplifiedProcessor(resolvedProcessorType);
  const isRecruitment = resolvedProcessorType === 'recruitment';

  const [draft, setDraft] = useState<DraftState>(DEFAULT_DRAFT);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [initializedKey, setInitializedKey] = useState<string | null>(null);
  const [recruitmentOutlookStatus, setRecruitmentOutlookStatus] =
    useState<RecruitmentOutlookStatus | null>(null);

  useEffect(() => {
    if (!open) {
      setInitializedKey(null);
      return;
    }
    if (mode === 'edit' && source) {
      const key = source.id || source.sourceKey;
      if (initializedKey !== key) {
        setDraft(buildDraftFromSource(source));
        setInitializedKey(key);
      }
    } else if (mode === 'create' && createDefaults) {
      const key = `create:${createDefaults.sourceKey}:${createDefaults.uiFolderPrefix}`;
      if (initializedKey !== key) {
        setDraft(buildDraftFromDefaults(createDefaults));
        setInitializedKey(key);
      }
    } else if (mode === 'empty') {
      if (initializedKey !== 'empty') {
        setDraft(DEFAULT_DRAFT);
        setInitializedKey('empty');
      }
    }
  }, [open, mode, source, createDefaults, initializedKey]);

  const updateDraft = <K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (!open || !isRecruitment) {
      setRecruitmentOutlookStatus(null);
      return;
    }
    let cancelled = false;
    getRecruitmentOutlookStatus(apiBaseUrl)
      .then((status) => {
        if (!cancelled) setRecruitmentOutlookStatus(status);
      })
      .catch(() => {
        if (!cancelled) setRecruitmentOutlookStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, open, isRecruitment]);

  const handleSave = async () => {
    if (mode === 'empty') return;
    setSaving(true);
    try {
      if (mode === 'edit' && source) {
        const updated = await updateEmailCrawlerSource(apiBaseUrl, source.sourceKey, {
          displayName: draft.displayName,
          mailboxAddress: draft.mailboxAddress,
          enabled: draft.enabled,
          scheduleCron: draft.scheduleCron.trim(),
          timezone: draft.timezone,
          lookbackMinutes: draft.lookbackMinutes,
          maxEmailsPerRun: draft.maxEmailsPerRun,
          s3Prefix: draft.s3Prefix,
          uiFolderPrefix: normalizeUiFolder(draft.uiFolderPrefix),
          subjectFilters: splitFilterText(draft.subjectFilter),
          senderFilters: splitFilterText(draft.senderFiltersText),
        });
        onSourceUpdated?.(updated);
        toast({
          title: 'Đã lưu cấu hình',
          description: `Cấu hình đã được cập nhật.`,
        });
      } else if (mode === 'create' && createDefaults) {
        const created = await createEmailCrawlerSource(apiBaseUrl, {
          sourceKey: draft.sourceKey.trim(),
          displayName: draft.displayName.trim() || draft.sourceKey.trim(),
          mailboxAddress: draft.mailboxAddress.trim(),
          processorType: createDefaults.processorType,
          enabled: draft.enabled,
          scheduleCron: draft.scheduleCron.trim(),
          timezone: draft.timezone.trim() || 'Asia/Ho_Chi_Minh',
          lookbackMinutes: draft.lookbackMinutes,
          maxEmailsPerRun: draft.maxEmailsPerRun,
          s3Prefix: draft.s3Prefix.trim(),
          uiFolderPrefix: normalizeUiFolder(draft.uiFolderPrefix),
          subjectFilters: splitFilterText(draft.subjectFilter),
          senderFilters: splitFilterText(draft.senderFiltersText),
        });
        onSourceCreated?.(created);
        toast({
          title: 'Đã tạo cấu hình crawler',
          description: `${created.sourceKey} đã được tạo.`,
        });
        onOpenChange(false);
      }
    } catch (err) {
      toast({
        title:
          mode === 'create' ? 'Lỗi tạo cấu hình crawler' : 'Lỗi lưu cấu hình crawler',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    if (!source) return;
    setRunning(true);
    try {
      const result = await runEmailCrawlerSource(apiBaseUrl, source.sourceKey);
      toast({
        title: 'Đã gửi yêu cầu chạy crawler',
        description: `Trạng thái: ${result.status} (${result.result})`,
      });
    } catch (err) {
      toast({
        title: 'Lỗi chạy crawler',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setRunning(false);
    }
  };

  // Thư mục S3 `bld-reports/`: chỉ có cấu hình BLD Reports, không có cấu hình crawler email.
  if (bldReports) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90dvh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Báo cáo Ban lãnh đạo</DialogTitle>
            <DialogDescription>
              Cấu hình nguồn dữ liệu SharePoint, lịch chạy và gửi báo cáo vào Teams.
            </DialogDescription>
          </DialogHeader>
          <BldReportsConfigForm apiBaseUrl={apiBaseUrl} onClose={() => onOpenChange(false)} />
        </DialogContent>
      </Dialog>
    );
  }

  if (mode === 'empty') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Cấu hình Email Crawler</DialogTitle>
            <DialogDescription>
              Không có cấu hình crawler cho thư mục hiện tại.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const isAuthReauth = source?.auth?.status === 'reauth_required';
  const isAuthConnected = source?.auth?.status === 'connected';
  const runNowDisabled = !isAuthConnected || isAuthReauth || saving || running;
  const canSave =
    !saving && Boolean(draft.mailboxAddress.trim()) && (isRecruitment || isAuthConnected || !simplified);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? simplified
                ? isRecruitment
                  ? 'Cấu hình tuyển dụng'
                  : 'Tạo Email Scheduler'
                : isRecruitment
                  ? 'Cấu hình tuyển dụng'
                  : 'Tạo Email Scheduler'
              : simplified
                ? isRecruitment
                  ? 'Cấu hình tuyển dụng'
                  : 'Cấu hình Email Scheduler'
                : isRecruitment
                  ? 'Cấu hình tuyển dụng'
                  : 'Cấu hình Email Scheduler'}
          </DialogTitle>
          {/* <DialogDescription>
            {mode === 'create' && createDefaults
              ? `Source mới: ${draft.sourceKey} · Thư mục: ${createDefaults.uiFolderPrefix}`
              : source
                ? `Source: ${source.sourceKey} · Hộp thư: ${source.mailboxAddress}`
                : ''}
          </DialogDescription> */}
        </DialogHeader>

        <div className="min-h-0 space-y-4 overflow-y-auto py-2 pr-1">
          {/* Outlook auth status (always shown for the new modal) */}
          {simplified && !isRecruitment && (
            <div className="rounded-md border p-3 space-y-2">
              <p className="text-sm text-muted-foreground">Chưa xác thực Outlook</p>
            </div>
          )}

          {isRecruitment && (
            <div className="rounded-md border p-3 space-y-2">
              {recruitmentOutlookStatus === null ? (
                <p className="text-sm text-muted-foreground">Đang kiểm tra cấu hình Outlook...</p>
              ) : recruitmentOutlookStatus.configured ? (
                <div className="flex items-center gap-1.5 text-sm text-green-700">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                  Đã cấu hình
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-sm text-red-700">
                    <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
                    Thiếu cấu hình client_secret
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Thiếu: {recruitmentOutlookStatus.missingVars.join(', ')}
                  </p>
                </div>
              )}
            </div>
          )}

          {isRecruitment && (
            <div className="space-y-1">
              <Label htmlFor="recruitment-mailbox" className="text-xs text-muted-foreground">
                Địa chỉ hộp thư
              </Label>
              <Input
                id="recruitment-mailbox"
                value={draft.mailboxAddress}
                onChange={(e) => updateDraft('mailboxAddress', e.target.value)}
                placeholder="tuyendung@hoaphatdungquat.vn"
                disabled={saving}
              />
            </div>
          )}

          {/* Account status block (edit mode, any processor) */}
          {mode === 'edit' && source && !simplified && (
            <div className="rounded-md border p-3 space-y-2">
              {source.auth ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-sm">
                    <span
                      className={`w-2 h-2 rounded-full inline-block ${
                        isAuthReauth ? 'bg-amber-500' : 'bg-green-500'
                      }`}
                    ></span>
                    <span>
                      {source.auth.authenticatedAccountEmail ?? 'Outlook'} ·{' '}
                      {isAuthReauth ? 'Cần kết nối lại' : 'Đã kết nối'}
                    </span>
                  </div>
                  {isAuthReauth && source.auth.reauthReason && (
                    <p className="text-xs text-muted-foreground">
                      Lý do: {source.auth.reauthReason}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Chưa có tài khoản Outlook cho source này.
                </p>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleRun}
                disabled={runNowDisabled}
              >
                {running ? 'Đang chạy...' : 'Chạy ngay'}
              </Button>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Switch
              id="enabled"
              checked={draft.enabled}
              onCheckedChange={(checked) => updateDraft('enabled', checked)}
              disabled={saving}
            />
            <Label htmlFor="enabled">Bật/Tắt</Label>
          </div>

          {/* Cron schedule: simplified form uses VN-time Select, dense form uses free-form Input */}
          {simplified && !isRecruitment ? (
            <div className="space-y-1">
              <Label htmlFor="crawler-cron" className="text-xs text-muted-foreground">
                Lịch chạy hằng ngày
              </Label>
              <Select
                value={utcCronToVnTime(draft.scheduleCron) || ''}
                onValueChange={(vnTime) => updateDraft('scheduleCron', vnTimeToUtcCron(vnTime))}
                disabled={saving}
              >
                <SelectTrigger id="crawler-cron">
                  <SelectValue placeholder="Chọn giờ chạy" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {VN_TIME_OPTIONS.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {utcCronToVnTime(draft.scheduleCron) && (
                <p className="text-xs text-muted-foreground">
                  Hằng ngày lúc {utcCronToVnTime(draft.scheduleCron)} (giờ VN)
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="cron" className="text-xs text-muted-foreground">Cron</Label>
              <Input
                id="cron"
                value={draft.scheduleCron}
                onChange={(e) => updateDraft('scheduleCron', e.target.value)}
                disabled={saving}
              />
              {isRecruitment && (
                <p className="text-xs text-muted-foreground">
                  Cron 5 trường, tính theo giờ UTC. Ví dụ: * * * * * chạy mỗi phút.
                </p>
              )}
            </div>
          )}

          {/* Subject filters — one filter per line, in both the simplified and the dense form */}
          {simplified ? (
            <div className="space-y-1">
              <Label htmlFor="crawler-subject" className="text-xs text-muted-foreground">
                Bộ lọc tiêu đề hoặc nội dung (mỗi dòng một chuỗi)
              </Label>
              <textarea
                id="crawler-subject"
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={draft.subjectFilter}
                onChange={(e) => updateDraft('subjectFilter', e.target.value)}
                placeholder="Ứng tuyển"
                disabled={saving}
              />
            </div>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="subjects" className="text-xs text-muted-foreground">
                Subject filters (mỗi dòng một chuỗi)
              </Label>
              <textarea
                id="subjects"
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={draft.subjectFilter}
                onChange={(e) => updateDraft('subjectFilter', e.target.value)}
                disabled={saving}
              />
            </div>
          )}

          {/* Dense-form-only fields */}
          {!simplified && (
            <>
              <div className="space-y-1">
                <Label htmlFor="sourceKey" className="text-xs text-muted-foreground">
                  Source key
                </Label>
                <Input
                  id="sourceKey"
                  value={draft.sourceKey}
                  onChange={(e) => updateDraft('sourceKey', e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="displayName" className="text-xs text-muted-foreground">
                  Tên hiển thị
                </Label>
                <Input
                  id="displayName"
                  value={draft.displayName}
                  onChange={(e) => updateDraft('displayName', e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="mailboxAddress" className="text-xs text-muted-foreground">
                  Địa chỉ hộp thư
                </Label>
                <Input
                  id="mailboxAddress"
                  value={draft.mailboxAddress}
                  onChange={(e) => updateDraft('mailboxAddress', e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="uiFolderPrefix" className="text-xs text-muted-foreground">
                  UI folder prefix
                </Label>
                <Input
                  id="uiFolderPrefix"
                  value={draft.uiFolderPrefix}
                  onChange={(e) => updateDraft('uiFolderPrefix', e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="timezone" className="text-xs text-muted-foreground">Timezone</Label>
                <Input
                  id="timezone"
                  value={draft.timezone}
                  onChange={(e) => updateDraft('timezone', e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="lookback" className="text-xs text-muted-foreground">
                    Lookback (phút)
                  </Label>
                  <Input
                    id="lookback"
                    type="number"
                    min={5}
                    value={draft.lookbackMinutes}
                    onChange={(e) => updateDraft('lookbackMinutes', Number(e.target.value))}
                    disabled={saving}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="max" className="text-xs text-muted-foreground">
                    Số email / lần
                  </Label>
                  <Input
                    id="max"
                    type="number"
                    min={1}
                    value={draft.maxEmailsPerRun}
                    onChange={(e) => updateDraft('maxEmailsPerRun', Number(e.target.value))}
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="prefix" className="text-xs text-muted-foreground">S3 prefix</Label>
                <Input
                  id="prefix"
                  value={draft.s3Prefix}
                  onChange={(e) => updateDraft('s3Prefix', e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="senders" className="text-xs text-muted-foreground">
                  Sender filters (mỗi dòng một chuỗi)
                </Label>
                <textarea
                  id="senders"
                  className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={draft.senderFiltersText}
                  onChange={(e) => updateDraft('senderFiltersText', e.target.value)}
                  disabled={saving}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Đóng
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving
              ? 'Đang lưu...'
              : mode === 'create'
                ? 'Tạo cấu hình'
                : 'Lưu cấu hình'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function splitFilterText(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
